import React, { useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { Bar } from 'react-chartjs-2'
import { Alert, Button, Card, CardBody, Table } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChartBar, faTable } from '@fortawesome/free-solid-svg-icons'
import Flex from '../../../common/Flex'
import Loader from '../../../common/Loader'
import useSWRFetch from '../../../../helpers/hooks/useSWRFetch'
import { getRewardsReport } from '../../../../helpers/api/rewardsAdmin'
import { grays, themeColors } from '../../../../helpers/utils'
import { formatNumber, formatPercent, formatPeriod, formatShortPeriod, formatUsd } from '../../../rewards/rewardsUtils'

const MONTHS = 6

// Ingreso en el color de la marca y costo en gris: el mismo par validado para
// daltonismo y contraste que usa la vista del administrador (HO-TASK-288).
const REVENUE_COLOR = themeColors.primary
const COST_COLOR = grays[600]

const capitalize = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text)

// Los últimos MONTHS meses hasta el mes de `endDate`. El mes en curso se corta
// en hoy para no pedir fechas futuras.
const buildMonths = (endDate) => {
  const last = moment(endDate, 'YYYY-MM-DD').startOf('month')
  const today = moment()
  return Array.from({ length: MONTHS }, (_, index) => {
    const month = last.clone().subtract(MONTHS - 1 - index, 'months')
    const end = moment.min(month.clone().endOf('month'), today)
    return { period: month.format('YYYY-MM'), startDate: month.format('YYYY-MM-DD'), endDate: end.format('YYYY-MM-DD') }
  }).filter((month) => month.startDate <= today.format('YYYY-MM-DD'))
}

// El backend no expone una serie mensual: se pide un reporte por mes.
const fetchEvolution = async (key, endMonth, condominiumId) => {
  const months = buildMonths(`${endMonth}-01`)
  const reports = await Promise.all(
    months.map((month) => getRewardsReport({ startDate: month.startDate, endDate: month.endDate, condominiumId }))
  )
  return months.map((month, index) => {
    const { revenue, cost, coins } = reports[index].data
    return {
      period: month.period,
      transactions: revenue.transactions,
      revenue: Number(revenue.gateway_commission_usd),
      cost: Number(cost.program_cost_usd),
      margin: Number(cost.net_margin_usd),
      marginPercent: Number(cost.net_margin_percent),
      hcIssued: Number(coins.hc_issued),
      hcRedeemed: Number(coins.hc_redeemed)
    }
  })
}

const SERIES = [
  { key: 'revenue', label: 'Ingreso por comisión', color: REVENUE_COLOR },
  { key: 'cost', label: 'Costo del programa', color: COST_COLOR }
]

const EvolutionChart = ({ rows }) => {
  const data = {
    labels: rows.map((row) => formatShortPeriod(row.period)),
    datasets: SERIES.map((serie) => ({
      label: serie.label,
      data: rows.map((row) => row[serie.key]),
      backgroundColor: serie.color,
      hoverBackgroundColor: serie.color,
      barPercentage: 0.8,
      categoryPercentage: 0.7,
      maxBarThickness: 28
    }))
  }

  const options = {
    maintainAspectRatio: false,
    legend: { display: false },
    tooltips: {
      mode: 'index',
      intersect: false,
      callbacks: {
        title: ([item]) => capitalize(formatPeriod(rows[item.index].period)),
        label: (item, chart) => `${chart.datasets[item.datasetIndex].label}: ${formatUsd(item.yLabel)}`,
        footer: ([item]) => `Margen neto: ${formatUsd(rows[item.index].margin)}`
      }
    },
    scales: {
      yAxes: [
        {
          ticks: { min: 0, callback: (value) => `$${formatNumber(value)}`, fontColor: grays[600], maxTicksLimit: 5 },
          gridLines: { color: grays[200], zeroLineColor: grays[300], drawBorder: false }
        }
      ],
      xAxes: [{ ticks: { fontColor: grays[600], maxRotation: 0, autoSkip: true, autoSkipPadding: 16 }, gridLines: { display: false } }]
    }
  }

  return (
    <div style={{ height: 260 }}>
      <Bar data={data} options={options} />
    </div>
  )
}

const EvolutionTable = ({ rows }) => (
  <Table size="sm" responsive className="fs--1 mb-0">
    <thead className="bg-200 text-900">
      <tr>
        <th>Mes</th>
        <th className="text-right">Pagos</th>
        <th className="text-right text-nowrap">Ingreso</th>
        <th className="text-right">Costo</th>
        <th className="text-right">Margen</th>
        <th className="text-right text-nowrap">Coins emitidas</th>
        <th className="text-right text-nowrap">Coins canjeadas</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((row) => (
        <tr key={row.period}>
          <td className="text-nowrap">{capitalize(formatPeriod(row.period))}</td>
          <td className="text-right">{formatNumber(row.transactions)}</td>
          <td className="text-right text-nowrap">{formatUsd(row.revenue)}</td>
          <td className="text-right text-nowrap">{formatUsd(row.cost)}</td>
          <td className="text-right text-nowrap font-weight-semi-bold">
            {formatUsd(row.margin)} <span className="text-600 font-weight-normal">({formatPercent(row.marginPercent)})</span>
          </td>
          <td className="text-right text-nowrap">{formatNumber(row.hcIssued, 2)}</td>
          <td className="text-right text-nowrap">{formatNumber(row.hcRedeemed, 2)}</td>
        </tr>
      ))}
    </tbody>
  </Table>
)

// HO-TASK-289: evolución de ingreso, costo y margen en los últimos meses.
const ReportEvolution = ({ endDate, condominiumId }) => {
  const [showTable, setShowTable] = useState(false)
  const endMonth = moment(endDate, 'YYYY-MM-DD').format('YYYY-MM')
  const { data: rows, error, isLoading, mutate } = useSWRFetch(
    ['rewards-report-evolution', endMonth, condominiumId],
    fetchEvolution,
    { revalidateOnFocus: false }
  )

  return (
    <Card className="mb-3">
      <CardBody>
        <Flex justify="between" align="start" className="flex-wrap mb-2">
          <div className="mr-2">
            <h5 className="fs-0 mb-1">Evolución de los últimos {MONTHS} meses</h5>
            <p className="fs--1 text-600 mb-0">Ingreso por comisión contra el costo del programa, mes a mes.</p>
          </div>
          {rows && (
            <Button color="link" size="sm" className="px-0" onClick={() => setShowTable((value) => !value)}>
              <FontAwesomeIcon icon={showTable ? faChartBar : faTable} className="mr-1" />
              {showTable ? 'Ver gráfico' : 'Ver tabla'}
            </Button>
          )}
        </Flex>

        {isLoading && <Loader padding="py-5" />}
        {error && (
          <Alert color="danger" className="fs--1 mb-0">
            No pudimos cargar la evolución mensual.
            <Button color="link" size="sm" className="ml-1 p-0 align-baseline fs--1" onClick={() => mutate()}>
              Reintentar
            </Button>
          </Alert>
        )}
        {rows && (
          <>
            {!showTable && (
              <Flex align="center" className="fs--1 text-700 flex-wrap mb-2">
                {SERIES.map((serie) => (
                  <span key={serie.key} className="d-inline-flex align-items-center mr-3">
                    <span className="d-inline-block rounded mr-1" style={{ width: 10, height: 10, background: serie.color }} />
                    {serie.label}
                  </span>
                ))}
              </Flex>
            )}
            {showTable ? <EvolutionTable rows={rows} /> : <EvolutionChart rows={rows} />}
          </>
        )}
      </CardBody>
    </Card>
  )
}

ReportEvolution.propTypes = { endDate: PropTypes.string.isRequired, condominiumId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) }

export default ReportEvolution
